import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {useAnonymousAuth} from '../AnonymousAuthProvider/AnonymousAuthProvider';
import {useFirebaseRemoteConfig} from '../FirebaseRemoteConfigProvider/FirebaseRemoteConfigProvider';
import {usePaywallAccess} from '@src/Providers/PaywallProvider/PaywallProvider';
import {
  pageRequiresAuth,
  elementRequiresAuth,
} from '@src/Services/AccessControl/accessRules';
import {contentAccessService} from '@src/Services/ContentAccess/ContentAccessService';
import {Routes} from '@src/Constant/routes';
import {
  useRouteChange,
  UseRouteChangeCallbackParams,
} from '@src/Hooks/useRouteChange';
import {ensureCleanLocalePrefix} from '@src/utils/url/ensureCleanLocalePrefix';
import {isVodacomSsoInProgress} from '@src/Hooks/useVodacomAutoLogin';

interface AuthGuardContextType {
  checkPageAccess: (route: string) => boolean;
  checkElementAccess: (element: HTMLElement) => boolean;
  showAuthModal: (reason?: string) => void;
}

const AuthGuardContext = createContext<AuthGuardContextType>({
  checkPageAccess: () => true,
  checkElementAccess: () => true,
  showAuthModal: () => {},
});

export const useAuthGuard = () => {
  const context = useContext(AuthGuardContext);
  if (!context) {
    throw new Error('useAuthGuard must be used within AuthGuardProvider');
  }
  return context;
};

interface AuthGuardProviderProps {
  children: React.ReactNode;
}

/**
 * AuthGuardProvider - Provider unifié pour la sécurisation de l'application
 *
 * Fusion de GlobalAuthGuardProvider + ContentAccessProvider
 *
 * Responsabilités:
 * 1. Configuration du ContentAccessService (injection des dépendances)
 * 2. Interception des clics et navigation (protection des routes)
 * 3. Gestion des événements d'authentification
 * 4. Synchronisation des états d'auth
 */
const AuthGuardProvider: React.FC<AuthGuardProviderProps> = ({children}) => {
  const {isAnonymous, user, isInitialized} = useAnonymousAuth();
  const {anonymousAuthEnabled} = useFirebaseRemoteConfig();
  const {canSeeContent, canPreview} = usePaywallAccess();

  // Sync paywall methods into ContentAccessService (legacy bridge)
  useEffect(() => {
    contentAccessService.setPaywallMethods({
      canSeeContent: (accessType?: string) => canSeeContent(accessType as any),
      canPreview,
    });
  }, [canSeeContent, canPreview]);

  useEffect(() => {
    if (anonymousAuthEnabled) {
      contentAccessService.setAnonymousAuthMethod(async (): Promise<boolean> => {
        if (!anonymousAuthEnabled) return false;
        if (user && isAnonymous) return true;
        return false;
      });
    }
  }, [anonymousAuthEnabled, user, isAnonymous]);

  useEffect(() => {
    contentAccessService.updateAuthState(!!user && !isAnonymous, isAnonymous);
  }, [user, isAnonymous, isInitialized]);

  useEffect(
    function recheckRouteAccessOnAuthChange() {
      if (!isInitialized) return;
      if (isVodacomSsoInProgress()) return;
      const currentPath = window.location.pathname;
      handleRouteChange({
        currentPath,
        previousPath: '',
        currentSearch: '',
        previousSearch: '',
      });
    },
    [isInitialized, user, isAnonymous],
  );

  // === 2. PROTECTION DES ROUTES ET ÉLÉMENTS (ex-GlobalAuthGuardProvider) ===

  const showAuthModal = (reason?: string, redirectTo = Routes.HOME) => {
    const isLoggedIn = !!user && !isAnonymous;
    const shouldShowModal = (isAnonymous && user) || (!user && !isLoggedIn);
    if (!shouldShowModal) return;

    contentAccessService.checkContentAccess(
      {
        contentType: 'article',
        contentId: `protected-route-${window.location.pathname}`,
        redirectTo: redirectTo || window.location.pathname,
        metadata: {title: reason || 'Route protégée'},
      },
      {forceCheck: true},
    );
  };

  const checkPageAccess = (route: string): boolean => {
    if (!isInitialized) return true;
    if (isVodacomSsoInProgress()) return true;

    const accessRule = pageRequiresAuth(route);
    const isLoggedIn = !!user && !isAnonymous;

    if (accessRule && (!isLoggedIn || isAnonymous)) {
      showAuthModal(`Accès à la page: ${accessRule.description || route}`, route as Routes);
      return false;
    }

    return true;
  };

  const checkElementAccess = (element: HTMLElement): boolean => {
    if (!isInitialized) return true;
    if (isVodacomSsoInProgress()) return true;

    const accessRule = elementRequiresAuth(element);
    const isLoggedIn = !!user && !isAnonymous;

    if (accessRule && (!isLoggedIn || isAnonymous)) {
      showAuthModal(`Action: ${accessRule.description || 'Interaction utilisateur'}`);
      return false;
    }

    return true;
  };

  // === 3. INTERCEPTION GLOBALE ===

  const handleRouteChange = useCallback(
    ({
      currentPath,
      previousPath,
      currentSearch,
      previousSearch,
    }: UseRouteChangeCallbackParams) => {
      if (!isInitialized) return;
      if (isVodacomSsoInProgress()) return;

      const accessRule = pageRequiresAuth(currentPath);
      const isLoggedIn = !!user && !isAnonymous;

      let fullRedirectUrl: string;
      if (previousPath) {
        fullRedirectUrl = previousPath + (previousSearch || '');
      } else {
        fullRedirectUrl = currentPath + (currentSearch || '');
      }

      const cleanRedirectUrl = ensureCleanLocalePrefix(
        fullRedirectUrl,
      ) as Routes;

      if (accessRule && (!isLoggedIn || isAnonymous)) {
        showAuthModal(`Accès à la page: ${accessRule.description || currentPath}`, cleanRedirectUrl);
      }
    },
    [isInitialized, isAnonymous, user, showAuthModal],
  );

  // Surveiller les changements de route avec notre hook personnalisé
  useRouteChange(handleRouteChange, true); // immediate: true pour vérifier au montage

  // Intercepter les clics globalement
  useEffect(() => {
    if (!isInitialized) return;

    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      // Vérifier l'élément et ses parents
      let currentElement: HTMLElement | null = target;
      while (currentElement) {
        if (!checkElementAccess(currentElement)) {
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
        currentElement = currentElement.parentElement;
      }
    };

    document.addEventListener('click', handleGlobalClick, true);
    return () => document.removeEventListener('click', handleGlobalClick, true);
  }, [isInitialized, isAnonymous, user]);

  useEffect(() => {
    if (!isInitialized) return;

    const handleLinkClick = (event: MouseEvent) => {
      if (isVodacomSsoInProgress()) return;

      const target = event.target as HTMLAnchorElement;

      if (target.tagName === 'A' && target.href) {
        const url = new URL(target.href);
        const path = url.pathname;
        const accessRule = pageRequiresAuth(path);
        const isLoggedIn = !!user && !isAnonymous;

        if (accessRule && (!isLoggedIn || isAnonymous)) {
          event.preventDefault();
          showAuthModal(`Navigation vers: ${accessRule.description || path}`);
          return false;
        }
      }
    };

    document.addEventListener('click', handleLinkClick, true);
    return () => document.removeEventListener('click', handleLinkClick, true);
  }, [isInitialized, isAnonymous, user]);

  useEffect(() => {
    if (!isInitialized) return;

    const handleAuthRequired = (event: CustomEvent) => {
      if (isVodacomSsoInProgress()) return;

      const isLoggedIn = !!user && !isAnonymous;
      if (isLoggedIn) return;

      const {actionId, reason} = event.detail;
      showAuthModal(reason || `API call requires authentication: ${actionId || 'unknown action'}`);
    };

    window.addEventListener(
      'authRequired',
      handleAuthRequired as EventListener,
    );
    return () =>
      window.removeEventListener(
        'authRequired',
        handleAuthRequired as EventListener,
      );
  }, [isInitialized, isAnonymous, user, showAuthModal]);

  const providerValues = useMemo(
    () => ({
      checkPageAccess,
      checkElementAccess,
      showAuthModal,
    }),
    [checkPageAccess, checkElementAccess, showAuthModal],
  );

  return (
    <AuthGuardContext.Provider value={providerValues}>
      {children}
    </AuthGuardContext.Provider>
  );
};

export default AuthGuardProvider;
